import { selectKrcCurriculumGoalNode } from '@newStore/documentUI/transformContent/krcContentNodeSelectors';
import { selectClusterMinimumGoalConsistencyValidation } from '@newStore/validation/validationRules/clusterMinimumGoalConsistencyValidation';
import { selectKrcGoalIdentifierUniqueValidation } from '@newStore/validation/validationRules/krcGoalIdentifierUniqueValidation';
import { selectChangeMessageForKrcGoalRelations } from '@UI/aside/asideKrcGoalRelations/asideKrcGoalRelationsSelectors';
import {
  krcGoalRelationsValidationRules,
  krcPrecedesValidationRules,
  krcTogetherValidationRules,
} from '@newStore/validation/validationRules/krcGoalRelationsValidationRules';
import { autoFillIGoalIdentifier } from '@newStore/documentUI/documentUIState';
import {
  type BuildingBlockNodeConfig,
  type EditAsideKrcGoalRelations,
  RequiredType,
} from '@nodeTypeConfig/configTypes';
import { descriptionComponent } from '@nodeTypeConfig/helpers/editComponents/descriptionComponent';
import { identifierComponent } from '@nodeTypeConfig/helpers/editComponents/identifierComponent';
import { minimumGoalComponent } from '@nodeTypeConfig/helpers/editComponents/minimumGoalComponent';
import { titleComponent } from '@nodeTypeConfig/helpers/editComponents/titleComponent';

const krcBeforeRelationsComponent: EditAsideKrcGoalRelations = {
  component: 'krcGoalRelationsBefore',
  reactComponent: 'AsideKrcGoalRelations',
  label: 'Voor (welke doelen komen net voor dit doel?)',
  options: {
    relationType: 'PRECEDES_BEFORE',
  },
  asideChangeMessageSelector: selectChangeMessageForKrcGoalRelations,
  validations: krcPrecedesValidationRules,
};

const krcAfterRelationsComponent: EditAsideKrcGoalRelations = {
  component: 'krcGoalRelationsAfter',
  reactComponent: 'AsideKrcGoalRelations',
  label: 'Na (welke doelen komen net na dit doel?)',
  options: {
    relationType: 'PRECEDES_AFTER',
  },
  asideChangeMessageSelector: selectChangeMessageForKrcGoalRelations,
  validations: krcPrecedesValidationRules,
};

const krcHandledTogetherRelationsComponent: EditAsideKrcGoalRelations = {
  component: 'krcGoalRelationsHandledTogether',
  reactComponent: 'AsideKrcGoalRelations',
  label: 'Samen',
  options: {
    relationType: 'IS_HANDLED_TOGETHER',
  },
  asideChangeMessageSelector: selectChangeMessageForKrcGoalRelations,
  validations: krcTogetherValidationRules,
};

const krcMandatoryTogetherRelationsComponent: EditAsideKrcGoalRelations = {
  component: 'krcGoalRelationsMandatoryTogether',
  reactComponent: 'AsideKrcGoalRelations',
  label: 'Verplicht samen',
  options: {
    relationType: 'IS_MANDATORY_TOGETHER',
  },
  asideChangeMessageSelector: selectChangeMessageForKrcGoalRelations,
  validations: krcTogetherValidationRules,
};

const krcAlternativeRelationsComponent: EditAsideKrcGoalRelations = {
  component: 'krcGoalRelationsAlternative',
  reactComponent: 'AsideKrcGoalRelations',
  label: 'Alternatief',
  options: {
    relationType: 'IS_ALTERNATIVE_FOR',
  },
  asideChangeMessageSelector: selectChangeMessageForKrcGoalRelations,
  validations: krcGoalRelationsValidationRules,
};

const KRC_CURRICULUM_GOAL: BuildingBlockNodeConfig = {
  nodeSelector: selectKrcCurriculumGoalNode,
  information: {
    single: 'Doel',
    plural: 'Doelen',
  },
  buildingBlocks: [],
  edit: [
    {
      ...identifierComponent,
      width: 2,
      required: RequiredType.ERROR,
      readonly: true,
      validations: [selectKrcGoalIdentifierUniqueValidation],
    },
    {
      ...titleComponent,
      width: 10,
      required: RequiredType.ERROR,
    },
    {
      ...descriptionComponent,
      options: {
        toolbar: 'bold italic | numlist bullist | customLink unlink | undo redo | removeformat',
      },
    },
    {
      ...minimumGoalComponent,
      validations: [selectClusterMinimumGoalConsistencyValidation],
    },
    krcMandatoryTogetherRelationsComponent,
    krcHandledTogetherRelationsComponent,
    krcBeforeRelationsComponent,
    krcAfterRelationsComponent,
    krcAlternativeRelationsComponent,
  ],
  documentViewIdentifier: 'i',
  isCollapsible: true,
  onNewNodeDropped: {
    dispatchAction: autoFillIGoalIdentifier,
    focusOnField: 'title',
  },
};

export default KRC_CURRICULUM_GOAL;
